// path: src/pages/IPA/Negociacoes/pages/NegociacaoFornecedor/NegociacaoFornecedor.networkCompetitors.ts
//
// O detalhe de concorrência da linha de rede: as fontes respondem no grão de UMA loja do cliente,
// então a linha consolidada é buscada loja por loja e juntada aqui. ARMADILHA: o card mostra a MENOR
// MÉDIA por loja concorrente e a coluna da grade mostra o MENOR PREÇO — o card é sempre maior ou
// igual, os dois não batem por desenho, e juntar mais lojas não aproxima.
import { trackPromise } from 'react-promise-tracker';
import { Competitor } from '../../../../../@types/IPA/GerenciadorDePrecos';
import { suppliersModalService } from '../../../../../services/suppliersModalService';
import { SuppliersExhibitionType } from '../../../../../store/useSuppliersExhibitionStore';
import { getCompetitorsInfoSuppliers } from './NegociacaoFornecedor.services';
import { CompetitionSnapshotState, CompetitorPriceDTOProps, NegotiationCompetitorsInfoProps } from './NegociacaoFornecedor.types';

/** O agrupamento em que cada produto aparece numa linha só, consolidando todas as lojas do cliente. */
const NETWORK_EXHIBITION_TYPE: SuppliersExhibitionType = 'PRODUCT_SUPPLIERS_NETWORK';

/**
 * Quantas consultas de concorrência ficam no ar ao mesmo tempo. Uma linha de rede pode ter dezenas de
 * lojas, e disparar todas de uma vez castiga navegador e servidor. Seis é escolha de segurança, e não
 * medição: é o mesmo teto que o navegador aplica por origem, e acima disso a fila só sairia daqui.
 */
export const NETWORK_COMPETITOR_CONCURRENCY_LIMIT = 6;

/**
 * Quantas lojas a listagem pede por página. Cem é o maior tamanho que o próprio modal de lojas
 * oferece ao comprador, então é um tamanho que o servidor deste caminho já responde hoje.
 */
export const NETWORK_STORE_LIST_PAGE_SIZE = 100;

/**
 * Teto de páginas que a listagem percorre, para que uma resposta que se contradiga -- anunciando mais
 * páginas e devolvendo conteúdo -- não vire laço sem fim dentro da tela.
 */
export const NETWORK_STORE_LIST_MAX_PAGES = 20;

type NetworkCompetitorsError = Error & { isNetworkCompetitorsError: true };

/**
 * O erro que a tela usa para RECUSAR o card, com a frase que o comprador lê. Não é classe própria de
 * propósito: `instanceof` sobre classe que estende `Error` não é confiável no alvo de compilação
 * deste projeto, e a marca em propriedade é conferível sem depender disso.
 */
const networkCompetitorsError = (message: string): NetworkCompetitorsError => Object.assign(new Error(message), { isNetworkCompetitorsError: true as const });

/** Diz se a recusa veio deste caminho, e não de uma falha qualquer de rede. */
export const isNetworkCompetitorsError = (error: unknown): error is NetworkCompetitorsError =>
  typeof error === 'object' && error !== null && (error as { isNetworkCompetitorsError?: unknown }).isNetworkCompetitorsError === true;

/**
 * A frase de recusa quando a própria listagem de lojas da rede falha. Diz o que aconteceu e o que
 * fazer, porque "erro ao carregar" não diz nem uma coisa nem outra.
 */
export const networkStoreListFailureMessage = (): string =>
  'Não foi possível listar as lojas da rede para buscar a concorrência. O detalhe não é exibido, ' +
  'nem pela metade: feche e abra a linha de novo em alguns instantes.';

/**
 * A frase de recusa quando a concorrência de uma das lojas falha. O card NÃO é montado com o que
 * chegou: card parcial parece completo, e o comprador negociaria contra um mercado menor que o real.
 */
export const networkStoreCompetitorFailureMessage = (failedStoreId: string, storeCount: number): string =>
  `Não foi possível buscar a concorrência da loja ${failedStoreId}, de ${storeCount} lojas da rede. ` +
  'O detalhe não é exibido pela metade, porque um card com parte dos concorrentes parece completo: ' +
  'feche e abra a linha de novo em alguns instantes.';

/** A identidade da bandeira concorrente, do jeito que o card a exibe: o nome, sem caixa nem espaço. */
const flagKeyOf = (entry: CompetitorPriceDTOProps): string =>
  String(entry?.competitorStore ?? '')
    .trim()
    .toUpperCase();

/** O preço da bandeira, ou ausência. Zero e negativo não são preço, e texto não é número. */
const priceOf = (entry: CompetitorPriceDTOProps): number | null => {
  const price = entry?.retailPrice;

  if (typeof price !== 'number' || !Number.isFinite(price) || price <= 0) return null;

  return price;
};

/**
 * Entre dois representantes da MESMA bandeira, qual fica. Quem tem preço vence quem não tem -- uma
 * bandeira sem preço em uma loja e com preço em outra tem preço na rede. Entre dois com preço, o
 * menor, que é a regra do motor. Empate mantém o primeiro, e a ordenação da lista resolve o resto.
 */
const chooseFlagRepresentative = (incumbent: CompetitorPriceDTOProps, challenger: CompetitorPriceDTOProps): CompetitorPriceDTOProps => {
  const incumbentPrice = priceOf(incumbent);
  const challengerPrice = priceOf(challenger);

  if (challengerPrice === null) return incumbent;
  if (incumbentPrice === null) return challenger;

  return challengerPrice < incumbentPrice ? challenger : incumbent;
};

/**
 * A ordem em que as bandeiras aparecem: menor preço primeiro, empate desfeito pelo nome da bandeira.
 * Bandeira sem preço vai para o fim -- ela não pode disputar a primeira posição, que é a posição
 * comparável com o número da coluna da grade.
 */
const compareFlags = (left: CompetitorPriceDTOProps, right: CompetitorPriceDTOProps): number => {
  const leftPrice = priceOf(left);
  const rightPrice = priceOf(right);

  if (leftPrice === null && rightPrice === null) return flagKeyOf(left).localeCompare(flagKeyOf(right), 'pt-BR');
  if (leftPrice === null) return 1;
  if (rightPrice === null) return -1;
  if (leftPrice !== rightPrice) return leftPrice - rightPrice;

  return flagKeyOf(left).localeCompare(flagKeyOf(right), 'pt-BR');
};

/**
 * A lista de bandeiras da rede: as respostas de todas as lojas, deduplicadas por bandeira, ficando
 * com o menor preço entre os representantes e com desempate por nome da bandeira.
 */
export const mergeNetworkCompetitorFlags = (entries: CompetitorPriceDTOProps[]): CompetitorPriceDTOProps[] => {
  const byFlag = new Map<string, CompetitorPriceDTOProps>();

  entries.forEach(entry => {
    if (!entry) return;

    const key = flagKeyOf(entry);
    const incumbent = byFlag.get(key);

    byFlag.set(key, incumbent ? chooseFlagRepresentative(incumbent, entry) : entry);
  });

  return Array.from(byFlag.values()).sort(compareFlags);
};

/**
 * O resumo do card -- preço médio, mais baixo e mais alto -- calculado a partir da lista de bandeiras
 * JÁ deduplicada. Somar os resumos por loja contaria a mesma bandeira uma vez por loja que a enxerga,
 * pesando a média pelas de maior cobertura. Sem bandeira com preço não há resumo: zero não é preço.
 */
export const summarizeNetworkCompetitorFlags = (flags: CompetitorPriceDTOProps[]): Competitor | null => {
  const prices = flags.map(priceOf).filter((price): price is number => price !== null);

  if (prices.length === 0) return null;

  const total = prices.reduce((sum, price) => sum + price, 0);

  return {
    competitorAverage: total / prices.length,
    competitorLower: Math.min(...prices),
    competitorHigher: Math.max(...prices),
    countCompetitorStore: flags.length,
  };
};

/**
 * A precedência dos estados da fotografia da concorrência quando as lojas discordam. Uma loja com
 * concorrente gravado manda sobre uma sem, e uma sem gravação manda sobre nenhuma informação: a frase
 * de vazio que o comprador lê tem de descrever o melhor caso encontrado, não o pior.
 */
const SNAPSHOT_STATE_PRECEDENCE: CompetitionSnapshotState[] = ['AVAILABLE', 'NO_COMPETITORS', 'UNAVAILABLE'];

const mergeSnapshotState = (responses: NegotiationCompetitorsInfoProps[]): CompetitionSnapshotState | undefined =>
  SNAPSHOT_STATE_PRECEDENCE.find(state => responses.some(response => response?.competitionSnapshotState === state));

/**
 * As N respostas por loja juntadas na resposta única que o card desenha. O resumo vai para o mesmo
 * campo que as lojas usaram, porque é esse campo que decide o título que o comprador lê -- concorrência
 * direta ou Infopanel.
 */
export const mergeNetworkCompetitorsInfo = (responses: NegotiationCompetitorsInfoProps[]): NegotiationCompetitorsInfoProps => {
  const flags = mergeNetworkCompetitorFlags(responses.flatMap(response => response?.competitorPriceDTO ?? []));

  const summary = summarizeNetworkCompetitorFlags(flags);

  const hasDirectCompetition = responses.some(response => Boolean(response?.competitor));

  const competitionSnapshotState = mergeSnapshotState(responses);

  return {
    competitorPriceDTO: flags,
    competitor: hasDirectCompetition ? summary : null,
    competitorInfoPanel: hasDirectCompetition ? null : summary,
    ...(competitionSnapshotState ? { competitionSnapshotState } : {}),
  };
};

/**
 * Executa `worker` sobre `items` mantendo no máximo `limit` execuções no ar. A primeira falha derruba
 * o conjunto: quem chama não monta card parcial, então resultado de item que ainda estava na fila não
 * tem valor nenhum.
 */
const mapWithConcurrencyLimit = async <TItem, TResult>(items: TItem[], limit: number, worker: (item: TItem) => Promise<TResult>): Promise<TResult[]> => {
  const results: TResult[] = new Array<TResult>(items.length);
  let nextIndex = 0;

  const runWorker = async (): Promise<void> => {
    while (nextIndex < items.length) {
      const index = nextIndex;
      nextIndex += 1;

      results[index] = await worker(items[index]);
    }
  };

  const workerCount = Math.max(1, Math.min(limit, items.length));

  await Promise.all(Array.from({ length: workerCount }, () => runWorker()));

  return results;
};

/**
 * As lojas da linha de rede, na mesma fonte que o modal "Lojas da rede" já usa -- e sem recorte de
 * cluster, que no grão rede não existe.
 */
const listNetworkStoreIds = async (productId: string): Promise<string[]> => {
  const storeIds: string[] = [];

  let page = 0;
  let totalPages = 1;

  while (page < totalPages && page < NETWORK_STORE_LIST_MAX_PAGES) {
    // eslint-disable-next-line no-await-in-loop
    const response = await suppliersModalService.getStoresData({
      page,
      size: NETWORK_STORE_LIST_PAGE_SIZE,
      sort: [],
      productId,
      suppliersExhibitionType: NETWORK_EXHIBITION_TYPE,
    });

    const content = response?.content ?? [];

    content.forEach(store => {
      const storeId = store?.storeId == null ? '' : String(store.storeId);

      if (storeId !== '' && !storeIds.includes(storeId)) storeIds.push(storeId);
    });

    // Página vazia encerra a varredura mesmo que a resposta anuncie mais páginas: sem isto, resposta
    // que se contradiz vira laço.
    if (content.length === 0) break;

    totalPages = typeof response?.totalPages === 'number' ? response.totalPages : page + 1;
    page += 1;
  }

  return storeIds;
};

export type NetworkCompetitorsRequest = {
  productId: string;
  productFamilyId: string | null;
  /** Informado apenas quando a tela quer a fotografia gravada no fechamento da negociação. */
  negotiationId?: string;
  /** A área do indicador de carregamento da linha, nomeada por quem chama. */
  loadingArea?: string;
};

const runNetworkCompetitorsFetch = async ({
  productId,
  productFamilyId,
  negotiationId,
  loadingArea,
}: NetworkCompetitorsRequest): Promise<NegotiationCompetitorsInfoProps> => {
  let storeIds: string[];

  try {
    storeIds = await listNetworkStoreIds(productId);
  } catch (error) {
    throw networkCompetitorsError(networkStoreListFailureMessage());
  }

  // Rede sem loja não é falha: é resposta vazia, e o card mostra a frase de concorrência
  // indisponível que ele já mostra hoje quando não há concorrente nenhum.
  if (storeIds.length === 0) return { competitorPriceDTO: [] };

  const responses = await mapWithConcurrencyLimit(storeIds, NETWORK_COMPETITOR_CONCURRENCY_LIMIT, async storeId => {
    try {
      return await getCompetitorsInfoSuppliers({ productId, storeId, productFamilyId, negotiationId }, loadingArea);
    } catch (error) {
      throw networkCompetitorsError(networkStoreCompetitorFailureMessage(storeId, storeIds.length));
    }
  });

  return mergeNetworkCompetitorsInfo(responses);
};

/**
 * O detalhe de concorrência da linha de rede: lista as lojas, busca a concorrência de cada uma com
 * limite de simultaneidade e junta tudo numa resposta só. Qualquer falha RECUSA o card inteiro. O
 * pedido inteiro é registrado na área de carregamento, para o indicador não apagar entre levas.
 */
export const getNetworkCompetitorsInfo = (request: NetworkCompetitorsRequest): Promise<NegotiationCompetitorsInfoProps> =>
  trackPromise(runNetworkCompetitorsFetch(request), request.loadingArea);
